/** @jsxRuntime automatic */
/** @jsxImportSource react */

/**
 * Mounts Clerk's sign-in / sign-up form on OUR origin.
 *
 * Three things here are load-bearing, each learned the hard way:
 *
 * 1. **The form lives on our origin, not Clerk's hosted portal.** A Clerk
 *    *development* instance needs ClerkJS running here to establish its
 *    dev-browser token. Bouncing to another domain never creates one, so every
 *    session check answers `dev-browser-missing` and sign-in can never stick —
 *    which the user experiences as an endless redirect loop.
 *
 * 2. **ClerkJS comes from Clerk's CDN, not our bundle.** The npm build
 *    lazy-loads chunks relative to its own script URL; `deno bundle` flattens
 *    that and mounting fails. It also keeps ~1.5MB out of our bundle.
 *
 * 3. **The UI is a SEPARATE bundle from the core** in v6. Core alone gives you
 *    "Clerk was not loaded with Ui components" the moment you mount. The UI
 *    script sets `window.__internal_ClerkUICtor`, which has to be passed into
 *    `load()`. Both script tags are emitted server-side by SignInPage.
 */

interface ClerkInstance {
  loaded?: boolean
  user?: unknown
  load(opts?: Record<string, unknown>): Promise<void>
  mountSignIn(node: HTMLDivElement, opts?: Record<string, unknown>): void
  mountSignUp(node: HTMLDivElement, opts?: Record<string, unknown>): void
}

type ClerkCtor = new (publishableKey: string) => ClerkInstance

interface ClerkWindow {
  Clerk?: ClerkCtor | ClerkInstance
  __internal_ClerkUICtor?: unknown
}

const root = document.getElementById('clerk-root') as HTMLDivElement | null

function fail(message: string) {
  if (root) {
    root.innerHTML =
      `<p style="padding:1rem;border:1px solid currentColor;border-radius:.75rem;font-size:.875rem;max-width:28rem">${message}</p>`
  }
}

/** Both CDN scripts are async — wait for the globals rather than assume order. */
function waitFor<T>(get: () => T | undefined, label: string, timeoutMs = 20_000): Promise<T> {
  return new Promise((resolve, reject) => {
    const started = Date.now()
    const tick = () => {
      const value = get()
      if (value) return resolve(value)
      if (Date.now() - started > timeoutMs) return reject(new Error(`${label} did not load`))
      setTimeout(tick, 50)
    }
    tick()
  })
}

if (root) {
  const mode = root.dataset.mode === 'sign-up' ? 'sign-up' : 'sign-in'
  const afterUrl = root.dataset.after || '/account'
  const publishableKey = root.dataset.publishableKey ?? ''

  try {
    if (!publishableKey) throw new Error('missing publishable key')

    const w = globalThis as unknown as ClerkWindow
    const Ctor = await waitFor(() => w.Clerk, 'ClerkJS')
    const ClerkUI = await waitFor(() => w.__internal_ClerkUICtor, 'Clerk UI bundle')

    // Without data-clerk-publishable-key on the script tag, window.Clerk is the
    // constructor — which is what we want, so we control load() options.
    const clerk: ClerkInstance = typeof Ctor === 'function'
      ? new (Ctor as ClerkCtor)(publishableKey)
      : Ctor as ClerkInstance

    if (!clerk.loaded) {
      await clerk.load({ ui: { ClerkUI }, signInUrl: '/sign-in', signUpUrl: '/sign-up' })
    }

    // Clear the server-rendered "Loading sign-in…" placeholder — Clerk appends
    // to this node rather than replacing its contents.
    root.textContent = ''

    if (clerk.user) {
      // Already signed in — skip the form entirely.
      globalThis.location.replace(afterUrl)
    } else if (mode === 'sign-up') {
      clerk.mountSignUp(root, { forceRedirectUrl: afterUrl, signInUrl: '/sign-in' })
    } else {
      clerk.mountSignIn(root, { forceRedirectUrl: afterUrl, signUpUrl: '/sign-up' })
    }
  } catch (err) {
    console.error('[clerk] failed to load', err)
    fail(
      'Sign-in could not load. Check that this origin is allowed in the Clerk dashboard, ' +
        'and that nothing is blocking scripts from Clerk.',
    )
  }
}
